import { Component, Inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { ImageService } from 'src/app/services/image.service';
import { selectOrgSettings } from 'src/app/store/auth/auth.selector';
import { isBenefitReceiptEnabled } from 'src/app/utility/common-logic';
import {
  isSalesReceiptUploadAllowedToday,
  isSalesReceiptUploadEnabled,
} from 'src/app/utility/receipt-upload-logic';
import { ImgFile } from 'src/app/utility/model/images';
import { OrganizationSettings } from 'src/app/utility/model/organization';
import { AddReceiptImageComponent } from './add-receipt-image/add-receipt-image.component';
import { EditReceiptImageComponent } from './edit-receipt-image/edit-receipt-image.component';
import { ProcessReceiptImageComponent } from './process-receipt-image/process-receipt-image.component';

interface DialogData {
  dialogMode: boolean;
  newReceiptFlow: number;
  uploadImageFromReceiptForm: boolean;
}

@Component({
  selector: 'app-upload-receipt',
  templateUrl: './upload-receipt.component.html',
  styleUrls: ['./upload-receipt.component.scss'],
})
export class UploadReceiptComponent {
  image: ImgFile | undefined;
  orgSettings: OrganizationSettings;

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private dialogRef: MatDialogRef<UploadReceiptComponent>,
    private imageService: ImageService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private store: Store,
  ) {}

  ngOnInit() {
    this.store.pipe(select(selectOrgSettings)).subscribe((settings) => {
      this.orgSettings = settings;
    });

    if (this.data?.uploadImageFromReceiptForm) {
      this.uploadImage();
    }
  }

  get route() {
    if (
      isBenefitReceiptEnabled(this.orgSettings) &&
      !isSalesReceiptUploadEnabled(this.orgSettings)
    ) {
      return '/add-receipt';
    }
    if (isSalesReceiptUploadAllowedToday(this.orgSettings)) {
      return '/add-sales-receipt';
    }
    return this.router.url;
  }

  uploadImage() {
    const dialogRef = this.dialog.open(AddReceiptImageComponent, {
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: {
        dialogMode: true,
        image: this.image,
      },
    });
    dialogRef.afterClosed().subscribe(async (res) => {
      if (!!res) {
        this.image = res[0];
        this.imageService.setImageData(this.image);
        if (this.data?.newReceiptFlow === 1) {
          this.processReceiptImage();
        } else {
          this.router.navigate([this.route]);
          this.dialogRef.close();
        }
      }
    });
  }

  processReceiptImage() {
    const processReceiptDialogRef = this.dialog.open(
      ProcessReceiptImageComponent,
      {
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: {
          dialogMode: true,
          image: this.image,
        },
      },
    );
    processReceiptDialogRef.afterClosed().subscribe(async (res) => {
      if (res !== undefined) {
        if (res.image) {
          this.image = res.image;
          this.imageService.setImageData(this.image);
        }
        if (this.data.uploadImageFromReceiptForm) {
          this.dialogRef.close(res);
        } else {
          this.router.navigate([this.route], { state: { response: res } });
          this.dialogRef.close();
        }
      }
      if (res === undefined) {
        this.dialogRef.close();
      }
    });
  }

  editReceiptImage() {
    const editImageDialogRef = this.dialog.open(EditReceiptImageComponent, {
      maxWidth: '100vw',
      maxHeight: '90vh',
      data: {
        dialogMode: true,
        image: this.image,
        confirmImage: true,
      },
    });
    editImageDialogRef.afterClosed().subscribe(async (res) => {
      if (!!res) {
        this.image = res;
        this.imageService.setImageData(this.image);
        if (this.data?.newReceiptFlow === 1) {
          this.processReceiptImage();
        } else {
          this.router.navigate([this.route]);
          this.dialogRef.close();
        }
      }
    });
  }

  goToAddReceiptPage() {
    this.image = null;
    this.imageService.setImageData(this.image);
    this.router.navigate([this.route]);
  }
}
